import { Box } from '@chakra-ui/react';

import {
    FilterCheckboxItem,
    FilterCheckboxSingleProps,
} from '@app/components/molecules/filter-checkbox-group';
import {
    AMENITIES_LABELS,
    BEACH_DISTANCE_ANY_VALUE,
    BEACH_DISTANCE_OPTIONS,
    FILTER_CATEGORY_LABELS,
    FILTER_KEYS,
    ONLINE_BOOKING_LABEL,
    PROPERTY_TYPE_LABELS,
    PROVISION_LABELS,
    RATING_LABELS,
    RESERVATION_FEATURES_LABELS,
    ROOM_EQUIPMENT_LABELS,
} from '@app/consts/search-filters.consts';
import {
    AmenityApiMapping,
    PropertyTypeApiMapping,
    ProvisionApiMapping,
    ReservationFeaturesEnum,
} from '@app/enums';
import { RoomEquipmentApiMapping } from '@app/enums/room-equipment-api-mapping.enum';
import { convertReservationFeaturesToBookingOptions } from '@app/helpers/map-api-params.helpers';
import { SearchFilters } from '@app/models';

import {
    getNewHierarchicalSelectionValue,
    getNewMultipleCheckboxGroupValue,
    handleCheckboxChange,
    handleHierarchicalSelectionChange,
    handleMultipleCheckboxGroupValueChange,
} from './filter-change.handlers';
import { PRICE_RANGE_CONFIG } from './filter-items.consts';
import {
    AvailableFilter,
    AvailableFilterKey,
    CheckboxFilterGroup,
    CreateFilterItemsProps,
    FilterComponentType,
    FilterCreator,
    RadioFilterGroup,
    RangeSliderFilterGroup,
} from './filter-items.types';

type OnFilterChange = CreateFilterItemsProps['onFilterChange'];

interface MultiValuesOption {
    /** Local identifier stored in `SearchFilters` (numeric id or feature key). */
    value: string | number;
    /** Translated, user-facing label for this option. */
    label: string;
    icon?: JSX.Element;
}

/**
 * Resolves the available value string for an enum-backed option. The
 * `availableOptions` response returns enum *names* (e.g. 'Apartment',
 * 'Internet'), while our local options are keyed by numeric ids — so we
 * reverse-map through the enum. A few returned names diverge from the TS enum
 * keys, those are covered by `overrides`.
 */
const resolveEnumValue = (
    value: string | number,
    enumMap: Record<string, string | number>,
    overrides?: Record<number, string>,
): string | undefined => {
    const numericValue = Number(value);

    return overrides?.[numericValue] ?? (enumMap[numericValue] as string | undefined);
};

/**
 * Builds a lookup keyed by the value used in `availableOptions` `filter.values`,
 * pointing at the translated local option. This lets each filter use the
 * returned `values` as the source of truth and just map them to translations.
 */
const buildOptionsByValue = (
    options: MultiValuesOption[],
    getAvailableValue: (value: string | number) => string | undefined,
): Record<string, MultiValuesOption> =>
    options.reduce<Record<string, MultiValuesOption>>((acc, option) => {
        const availableValue = getAvailableValue(option.value);

        if (availableValue) {
            acc[availableValue] = option;
        }

        return acc;
    }, {});

/**
 * Turns the filter's returned `values` (source of truth) into checkbox items by
 * mapping each value to its translated option, dropping unknown/excluded ones.
 */
const buildItemsFromValues = (
    values: string[] | undefined,
    optionsByValue: Record<string, MultiValuesOption>,
    excludeValues?: string[],
): FilterCheckboxItem[] =>
    (values ?? [])
        .map(value => optionsByValue[value])
        .filter(
            (option): option is MultiValuesOption =>
                !!option && !excludeValues?.includes(String(option.value)),
        )
        .map(option => ({
            value: String(option.value),
            label: option.label,
            icon: option.icon,
        }));

/**
 * Returned category names that don't match their `PropertyTypeApiMapping` enum keys.
 */
const CATEGORY_VALUE_OVERRIDES: Record<number, string> = {
    [PropertyTypeApiMapping.WorkerAccommodation]: 'WorkerAccommodations',
};

/**
 * Maps the local rating thresholds to the rating band names from `availableOptions`.
 */
const RATING_VALUE_BY_VALUE: Record<string, string> = {
    '4.51': 'Sensational',
    '4': 'VeryGood',
    '3': 'Good',
    '2': 'Decent',
    '1': 'Average',
};

const CATEGORY_OPTIONS_BY_VALUE = buildOptionsByValue(PROPERTY_TYPE_LABELS, value =>
    resolveEnumValue(value, PropertyTypeApiMapping, CATEGORY_VALUE_OVERRIDES),
);

const MEALS_OPTIONS_BY_VALUE = buildOptionsByValue(PROVISION_LABELS, value =>
    resolveEnumValue(value, ProvisionApiMapping),
);

const AMENITY_OPTIONS_BY_VALUE = buildOptionsByValue(AMENITIES_LABELS, value =>
    resolveEnumValue(value, AmenityApiMapping),
);

const ROOM_EQUIPMENT_OPTIONS_BY_VALUE = buildOptionsByValue(ROOM_EQUIPMENT_LABELS, value =>
    resolveEnumValue(value, RoomEquipmentApiMapping),
);

const BOOKING_FEATURES_OPTIONS_BY_VALUE = buildOptionsByValue(
    Object.entries(RESERVATION_FEATURES_LABELS).map(([value, label]) => ({ value, label })),
    value => convertReservationFeaturesToBookingOptions([String(value)])?.[0],
);

const RATING_OPTIONS_BY_VALUE = buildOptionsByValue(
    RATING_LABELS.map(item => ({
        value: item.value,
        label: item.label,
        icon: <Box width={2} height={2} borderRadius="50%" backgroundColor={item.iconColor} />,
    })),
    value => RATING_VALUE_BY_VALUE[String(value)],
);

/**
 * Shared helper for every `MultiValues` filter (Amenity, Category, Meals,
 * BookingFeatures, ...). It builds its options from the filter's `values` (the
 * source of truth) via `optionsByValue`, wires the standard multi-select change
 * handler and returns a ready checkbox group. Per-key creators stay thin and
 * only pass their specifics.
 */
const createMultiValuesFilter = ({
    availableFilter,
    optionsByValue,
    filterKey,
    selectedValues,
    title,
    checkboxGroupType,
    onFilterChange,
    onValueChange,
    initialVisibleCount,
    excludeValues,
    prependCheckbox,
    getPredictedValue,
}: {
    availableFilter: AvailableFilter;
    optionsByValue: Record<string, MultiValuesOption>;
    filterKey: keyof SearchFilters;
    selectedValues: string[] | undefined;
    title: string;
    checkboxGroupType: CheckboxFilterGroup['checkboxGroupType'];
    onFilterChange: OnFilterChange;
    /** Overrides the default multi-select handler (e.g. hierarchical rating). */
    onValueChange?: (value: string, checked: boolean) => void;
    initialVisibleCount?: number;
    excludeValues?: string[];
    prependCheckbox?: FilterCheckboxSingleProps;
    getPredictedValue?:
        | ((value: string, checked: boolean) => string[])
        | ((value: string, checked: boolean) => number | null);
}): CheckboxFilterGroup | null => {
    const items = buildItemsFromValues(availableFilter.values, optionsByValue, excludeValues);

    if (!items.length && !prependCheckbox) {
        return null;
    }

    return {
        componentType: FilterComponentType.CHECKBOX_GROUP,
        checkboxGroupType,
        title,
        items,
        initialVisibleCount,
        prependCheckbox,
        filterKey,
        selectedValues: selectedValues || [],
        getPredictedValue:
            getPredictedValue ??
            ((value, checked) => getNewMultipleCheckboxGroupValue(selectedValues, value, checked)),
        onValueChange:
            onValueChange ??
            ((value, checked) => {
                handleMultipleCheckboxGroupValueChange(
                    filterKey,
                    selectedValues,
                    value,
                    checked,
                    onFilterChange,
                );
            }),
    };
};

/**
 * Shared helper for every `Range` filter (Price, ...). The numeric bounds come
 * from the returned `range`, falling back to local defaults when it's omitted.
 */
const createRangeFilter = ({
    availableFilter,
    title,
    unit,
    step,
    fallback,
    selectedRange,
    onRangeChange,
}: {
    availableFilter: AvailableFilter;
    title: string;
    unit?: string;
    step?: number;
    fallback: { min: number; max: number };
    selectedRange: [number | null | undefined, number | null | undefined];
    onRangeChange: (range: [number, number], min: number, max: number) => void;
}): RangeSliderFilterGroup => {
    const min = availableFilter.range?.min ?? fallback.min;
    const max = availableFilter.range?.max ?? fallback.max;

    return {
        componentType: FilterComponentType.RANGE_SLIDER,
        title,
        min,
        max,
        step,
        unit,
        selectedRange: [selectedRange[0] ?? min, selectedRange[1] ?? max],
        onChange: range => onRangeChange(range, min, max),
    };
};

const createBookingFeaturesFilter: FilterCreator = ({ availableFilter, filters, onFilterChange }) =>
    createMultiValuesFilter({
        availableFilter,
        optionsByValue: BOOKING_FEATURES_OPTIONS_BY_VALUE,
        // The realtime booking model is surfaced through the reservation switch instead.
        excludeValues: [ReservationFeaturesEnum.RealtimeBookingModel],
        filterKey: FILTER_KEYS.RESERVATION_FEATURES,
        selectedValues: filters.reservationFeatures,
        title: FILTER_CATEGORY_LABELS.RESERVATION_FEATURES,
        checkboxGroupType: CheckboxFilterGroup.RESERVATION_FEATURES,
        onFilterChange,
        prependCheckbox: {
            filterKey: 'onlineBooking',
            item: {
                label: ONLINE_BOOKING_LABEL,
                value: '1',
            },
            checked: filters?.onlineBooking === '1',
            onCheckedChange: details => {
                handleCheckboxChange(
                    'onlineBooking',
                    '1',
                    typeof details.checked === 'boolean' ? details.checked : false,
                    onFilterChange,
                );
            },
        },
    });

const createCategoryFilter: FilterCreator = ({ availableFilter, filters, onFilterChange }) =>
    createMultiValuesFilter({
        availableFilter,
        optionsByValue: CATEGORY_OPTIONS_BY_VALUE,
        filterKey: FILTER_KEYS.PROPERTY_TYPE,
        selectedValues: filters.propertyType,
        title: FILTER_CATEGORY_LABELS.PROPERTY_TYPE,
        checkboxGroupType: CheckboxFilterGroup.PROPERTY_TYPE,
        initialVisibleCount: 6,
        onFilterChange,
    });

const createMealsFilter: FilterCreator = ({ availableFilter, filters, onFilterChange }) =>
    createMultiValuesFilter({
        availableFilter,
        optionsByValue: MEALS_OPTIONS_BY_VALUE,
        filterKey: FILTER_KEYS.PROVISION,
        selectedValues: filters.provision,
        title: FILTER_CATEGORY_LABELS.PROVISION,
        checkboxGroupType: CheckboxFilterGroup.PROVISION,
        onFilterChange,
    });

const createAmenityFilter: FilterCreator = ({ availableFilter, filters, onFilterChange }) =>
    createMultiValuesFilter({
        availableFilter,
        optionsByValue: AMENITY_OPTIONS_BY_VALUE,
        filterKey: FILTER_KEYS.AMENITIES,
        selectedValues: filters.amenities,
        title: FILTER_CATEGORY_LABELS.AMENITIES,
        checkboxGroupType: CheckboxFilterGroup.AMENITIES,
        onFilterChange,
    });

const createRoomEquipmentFilter: FilterCreator = ({ availableFilter, filters, onFilterChange }) =>
    createMultiValuesFilter({
        availableFilter,
        optionsByValue: ROOM_EQUIPMENT_OPTIONS_BY_VALUE,
        filterKey: FILTER_KEYS.ROOM_EQUIPMENT,
        selectedValues: filters.roomEquipment,
        title: FILTER_CATEGORY_LABELS.ROOM_EQUIPMENT,
        checkboxGroupType: CheckboxFilterGroup.ROOM_EQUIPMENT,
        onFilterChange,
    });

const createRatingFilter: FilterCreator = ({ availableFilter, filters, onFilterChange }) =>
    createMultiValuesFilter({
        availableFilter,
        optionsByValue: RATING_OPTIONS_BY_VALUE,
        filterKey: FILTER_KEYS.RATING,
        title: FILTER_CATEGORY_LABELS.RATING,
        checkboxGroupType: CheckboxFilterGroup.RATING,
        onFilterChange,
        getPredictedValue: (value, checked) =>
            getNewHierarchicalSelectionValue(value, checked, filters.rating),
        // Rating is hierarchical: selecting a rating also marks every higher one.
        selectedValues: filters?.rating
            ? RATING_LABELS.filter(item => Number(item.value) >= Number(filters?.rating)).map(
                  item => String(item.value),
              )
            : [],
        onValueChange: (value, checked) =>
            handleHierarchicalSelectionChange(
                FILTER_KEYS.RATING,
                value,
                checked,
                filters.rating,
                onFilterChange,
            ),
    });

const createPriceFilter: FilterCreator = ({ availableFilter, filters, onFilterChange }) =>
    createRangeFilter({
        availableFilter,
        title: FILTER_CATEGORY_LABELS.PRICE_RANGE,
        unit: PRICE_RANGE_CONFIG.UNIT,
        step: PRICE_RANGE_CONFIG.STEP,
        fallback: { min: PRICE_RANGE_CONFIG.MIN, max: PRICE_RANGE_CONFIG.MAX },
        selectedRange: [filters.priceFrom, filters.priceTo],
        onRangeChange: (range, min, max) => {
            onFilterChange(FILTER_KEYS.PRICE_FROM, range[0] === min ? null : range[0]);
            onFilterChange(FILTER_KEYS.PRICE_TO, range[1] === max ? null : range[1]);
        },
    });

const getPredictedBeachFilterValue = (value: string) =>
    value === BEACH_DISTANCE_ANY_VALUE ? null : Number(value);

const createBeachDistanceFilter: FilterCreator = ({
    filters,
    onFilterChange,
}): RadioFilterGroup => ({
    filterKey: FILTER_KEYS.BEACH_DISTANCE,
    componentType: FilterComponentType.RADIO_GROUP,
    title: FILTER_CATEGORY_LABELS.BEACH_DISTANCE,
    items: BEACH_DISTANCE_OPTIONS,
    getPredictedValue: getPredictedBeachFilterValue,
    selectedValue:
        filters.beachDistance === null ? BEACH_DISTANCE_ANY_VALUE : String(filters.beachDistance),
    onValueChange: value => {
        onFilterChange(
            FILTER_KEYS.BEACH_DISTANCE,
            value === BEACH_DISTANCE_ANY_VALUE ? null : Number(value),
        );
    },
});

/**
 * Registry mapping each filter `key` to its dedicated creator. Add a new key
 * here (plus its `create*Filter` function above) to support a new filter.
 */
export const FILTER_CREATORS: Partial<Record<AvailableFilterKey, FilterCreator>> = {
    BookingFeatures: createBookingFeaturesFilter,
    Category: createCategoryFilter,
    Meals: createMealsFilter,
    RoomEquipment: createRoomEquipmentFilter,
    Amenity: createAmenityFilter,
    Rating: createRatingFilter,
    Price: createPriceFilter,
    BeachDistance: createBeachDistanceFilter,
};
